import { Material } from '@cutr/constants/cutlist';
import {
  ALL_CUSTOM_ARTICLES,
  FSC_CLAIM,
  PEFC_CLAIM,
} from '@cutr/constants/material';
import { createFuzzySearch, type FuzzySearcher } from '@cutr/kanna';
import i18n from 'i18next';
import React from 'react';

import {
  getCoreMaterials,
  getCoreMaterialsForGluingAndPressing,
  getEdgebandingMaterials,
  getHPLMaterials,
  getMaterial,
} from './materials';

export const MAX_SEARCH_RESULTS = 200;
export const DEFAULT_MATERIAL_LIST_SIZE = 100;

type SearchableMaterial = {
  name0: string;
  name1: string;
  name2: string;
  name3: string;
  name4: string;
  name5: string;
  name6: string;
  name7: string;
  name8: string;
  fullName: string;
  length: string;
  width: string;
  thickness: string;
  articleCode: string;
  supplierCode: string;
  category: string | string[];
  fscClaimLabel: string;
  pefcClaimLabel: string;
};

export const getSearchableMaterials = (materials: Material[]) => {
  return materials
    .filter(
      // we don't want custom edgebanding materials in the search.
      // for example, they should not show up for queries such as "bijpassend"
      (material) =>
        !(ALL_CUSTOM_ARTICLES as Material['articleCode'][]).includes(
          material.articleCode
        )
    )
    .map((m) => {
      const uniqueTokens = `${m.name} ${m.variationGroup?.name || ''}`
        .split(' ')
        .filter((token) => token.length)
        .filter((value, index, self) => self.indexOf(value) === index);
      const regex = new RegExp(`([0-9]+mm)|([0-9]+cm)`);
      const filtered = uniqueTokens.filter((token) => {
        return !regex.test(token);
      });

      const obj = filtered.reduce(
        (acc, val, index) => ({ ...acc, [`name${index}`]: val }),
        {}
      );

      const category = formatCategoryForSearch(m.type, m.name);

      const thickness = m.thicknessUM / 1000;
      return {
        ...obj,
        fullName: filtered.join(' '),
        length: String(m.lengthMM),
        width: String(m.widthMM),
        thickness: String(thickness),
        articleCode: m.articleCode,
        supplierCode: m.supplierCode,
        category,
        fscClaimLabel:
          m.fscClaim === FSC_CLAIM.fsc_other ? '' : m.fscClaimLabel || '',
        pefcClaimLabel:
          m.pefcClaim === PEFC_CLAIM.pefc_other ? '' : m.pefcClaimLabel || '',
      } as SearchableMaterial;
    });
};

const formatCategoryForSearch = (type: string, name: string) => {
  if (type === 'unknown') return '';

  // these are the same in Dutch as in English
  if (['hpl', 'osb', 'mdf', 'melamine', 'viroc'].includes(type)) {
    return type;
  }

  const keys = type.split('-');
  if (type === 'concrete-plywood') {
    keys.pop();
    keys.push('formply');
  }
  if (type === 'solid-wood' && name.toLowerCase().includes('bambo')) {
    keys.push('bamboo');
    keys.push(i18n.t('cutlist-form.materialTypes.bamboo'));
  }
  keys.push(i18n.t(`cutlist-form.materialTypes.${type}`));
  return keys;
};

type SearchInstances = {
  hpl: FuzzySearcher<SearchableMaterial>;
  core: FuzzySearcher<SearchableMaterial>;
  coreForGluingAndPressing: FuzzySearcher<SearchableMaterial>;
  edgeband: FuzzySearcher<SearchableMaterial>;
};
const searchInstances = {} as SearchInstances;

export type MaterialSearchType = keyof SearchInstances;

export const getMaterialSearcher = (type: keyof SearchInstances) =>
  searchInstances[type];

// Fuzzy search matches all query words (space separated, any order) within a
// single text, so we combine every searchable field into one string per
// material — this preserves cross-field queries like "berken 18".
const getSearchText = (m: SearchableMaterial): string[] => [
  [
    m.fullName,
    m.length,
    m.width,
    m.thickness,
    m.articleCode,
    ...(Array.isArray(m.category) ? m.category : [m.category]),
    m.fscClaimLabel,
    m.pefcClaimLabel,
  ]
    .filter(Boolean)
    .join(' '),
];

const buildSearcher = (materials: Material[]) =>
  createFuzzySearch(getSearchableMaterials(materials), {
    getText: getSearchText,
  });

export const buildMaterialSearchers = () => {
  searchInstances.core = buildSearcher(getCoreMaterials());
  searchInstances.coreForGluingAndPressing = buildSearcher(
    getCoreMaterialsForGluingAndPressing()
  );
  searchInstances.hpl = buildSearcher(getHPLMaterials());
  searchInstances.edgeband = buildSearcher(getEdgebandingMaterials());
};

// strip unit suffixes so "18mm" matches the stored dimension "18"
export const normalizeSearchTerm = (input: string) =>
  input.replace(/(\d+)\s*(mm|cm)\b/gi, '$1');

export const useMaterialSearch = (
  searchTerm: string,
  materialType: MaterialSearchType = 'core'
) => {
  const search = getMaterialSearcher(materialType);

  const searchResults = React.useMemo(() => {
    if (!searchTerm) {
      return [];
    }

    return search(normalizeSearchTerm(searchTerm))
      .slice(0, MAX_SEARCH_RESULTS)
      .map((r) => getMaterial(r.item.articleCode)!);
  }, [searchTerm, search]);

  return searchResults;
};
